import { useState, type KeyboardEvent, type RefObject } from 'react';
import { Trash2, TriangleAlert } from 'lucide-react';
import {
  type NomenclatureDto,
  type Unit,
} from '@eombor/contracts';
import { Badge, Input, cn } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { NomenclaturePicker } from '../../catalog/ui/NomenclaturePicker.js';
import { cellStockSummary, type CellStockIndex } from '../model/cell-stock.js';
import {
  formatQty,
  isLineReady,
  parseQty,
  qtyError,
  type ReceiptLineDraft,
} from '../model/receipt-line.js';
import { CellPicker, cellPlaceText } from './CellPicker.js';
import { ReservedCells } from './ReservedCells.js';
import type { CellDto } from '../../warehouse/api/warehouse.api.js';
import { unitText } from '../../../shared/i18n/labels.js';

/** Satr ustunlari — jadval boshi bilan BIR XIL shablondan chiziladi. */
export const RECEIPT_ROW_GRID =
  'grid grid-cols-[2rem_minmax(0,1fr)_13rem_9rem_7rem_2.25rem] items-start gap-3';

export interface ReceiptLineRowProps {
  line: ReceiptLineDraft;
  /** Ko'rinadigan satr raqami (1 dan) — hujjatdagi `lineNo` bilan bir xil tartib. */
  index: number;
  warehouseId: string;
  /** Tanlangan nomenklatura — birligi ham, nomi ham shundan olinadi. */
  nomenclature: NomenclatureDto | null;
  /** Tanlangan nomenklaturaning birligi — miqdor qoidasi shundan keladi. */
  unit: Unit | null;
  /** Ombordagi qoldiqning katak kesimi. `null` — hali yuklanmagan. */
  cellStock?: CellStockIndex | null;
  onChange: (patch: Partial<ReceiptLineDraft>) => void;
  onSelectNomenclature: (nomenclature: NomenclatureDto | null) => void;
  onRemove: () => void;
  /** Miqdordan keyin `Enter`: satr tayyor bo'lsa yangi satr ochiladi. */
  onCommit: () => void;
  /** Katak tekshirilgach fokus miqdorga o'tsin — sahifa hal qiladi. */
  onCellResolved?: (cell: CellDto | null) => void;
  cellRef?: RefObject<HTMLInputElement | null>;
  qtyRef?: RefObject<HTMLInputElement | null>;
  canRemove?: boolean;
}

/**
 * Kirim hujjatining bitta satri.
 *
 * Satr `<tr>` emas, to'r (grid): ichida tekshiriluvchi maydonlar, holat matni
 * va tugma turadi — `Table` esa MATNLI ma'lumot uchun. Ustunlar sarlavha
 * qatori bilan bitta shablondan (`RECEIPT_ROW_GRID`) chiziladi, shuning uchun
 * ular hech qachon siljib ketmaydi.
 *
 * "Tayyor emas" belgisi ataylab: satr yozilmasligini SAQLASHDA emas, aynan
 * shu yerda ko'rsatadi.
 *
 * Satr ostidagi izoh — katakning MANZILI va undagi qoldiq. Omborchi mahsulot
 * qo'shayotganda u yerda avval qancha borligini va qo'shgandan keyin qancha
 * bo'lishini KIRITISH PAYTIDA ko'rishi kerak: xato kiritilgani hisobotdan
 * emas, shu yerdan bilinsin.
 */
export const ReceiptLineRow = ({
  line,
  index,
  warehouseId,
  nomenclature,
  unit,
  cellStock = null,
  onChange,
  onSelectNomenclature,
  onRemove,
  onCommit,
  onCellResolved,
  cellRef,
  qtyRef,
  canRemove = true,
}: ReceiptLineRowProps) => {
  /* Tekshirilgan katak SATRDA saqlanadi: sahifaga faqat `cellId` kerak
     (hujjatga o'sha yoziladi), manzil va qoldiq esa shu satrning izohi.
     `CellPicker` kod o'zgarganda `null` yuboradi, ya'ni holat desinxron
     bo'lib qolmaydi. */
  const [cell, setCell] = useState<CellDto | null>(null);

  const error = qtyError(unit, line.qty);
  const ready = isLineReady(line, unit);

  const handleQtyKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    if (ready) onCommit();
  };

  /*
   * AJRATISH OGOHLANTIRISHI — to'siq EMAS.
   *
   * Katak boshqa mahsulot uchun ajratilgan bo'lsa skladchi buni bilishi
   * kerak, lekin ish to'xtamasligi ham kerak: shoshilinch holatda joyni
   * qidirib yurgandan ko'ra ogohlantirishni o'qib davom etgani yaxshi.
   * Shuning uchun bu `error` emas, `Badge`.
   */
  const reservedWarning =
    cell === null ||
    nomenclature === null ||
    cell.reserved.length === 0 ||
    // Ajratilganlardan BIRI shu mahsulot bo'lsa ogohlantirish kerak
    // emas: katak aynan shuning uchun ham ajratilgan.
    cell.reserved.some((item) => item.id === nomenclature.id)
      ? null
      : t('warehouse.cellReservedWarn', {
          name: cell.reserved.map((item) => item.name).join(', '),
        });

  /* Qoldiq izohi faqat katak TASDIQLANGANDA va nomenklatura tanlanganda
     ma'noga ega: ikkalasisiz "hozir qancha bor" degan savolning javobi yo'q. */
  const summary =
    cellStock === null || cell === null || nomenclature === null
      ? null
      : cellStockSummary(cellStock, cell.id, nomenclature.id);

  const added = parseQty(line.qty);
  const unitName = unit === null ? '' : unitText(unit);

  const nowText =
    summary === null
      ? null
      : !summary.known
        ? null
        : summary.currentQty === 0 && summary.others.length === 0
          ? // Bo'sh katak va nol qoldiq boshqa-boshqa narsa: birinchisida katak
            // umuman ishlatilmagan, ikkinchisida mahsulot chiqib ketgan.
            t('stock.cellStockEmpty')
          : t('stock.cellStockNow', { qty: formatQty(summary.currentQty), unit: unitName });

  const afterText =
    summary === null || !summary.known || added === null || error !== null
      ? null
      : t('stock.cellStockAfter', {
          qty: formatQty(summary.currentQty + added),
          unit: unitName,
        });

  const othersText =
    summary === null || summary.others.length === 0
      ? null
      : t('stock.cellStockOther', {
          names: summary.others.map((other) => `${other.code} — ${other.name}`).join(', '),
        });

  const placeText = cell === null ? null : cellPlaceText(cell);
  const noteParts = [placeText, nowText, afterText].filter((part) => part !== null);

  return (
    <li className="flex flex-col rounded-[var(--radius-sm)] px-1 py-1.5">
      <div className={RECEIPT_ROW_GRID}>
        <span className="tabular pt-2.5 text-xs text-text-faint">{index}</span>

        <NomenclaturePicker
          hiddenLabel
          label={t('stock.colNomenclature')}
          placeholder={t('stock.selectNomenclature')}
          value={nomenclature}
          onSelect={onSelectNomenclature}
        />

        <CellPicker
          hiddenLabel
          showPlace={false}
          label={t('stock.colCell')}
          warehouseId={warehouseId}
          /* Kod yozilganda ajratilgan kataklar ro'yxatning TEPASIDA
             turadi — ular tasodifan o'xshagan kod emas. */
          nomenclatureId={nomenclature?.id ?? null}
          value={line.cellCode}
          inputRef={cellRef}
          onChange={(code) => onChange({ cellCode: code })}
          onResolved={(resolved) => {
            setCell(resolved);
            onChange({ cellId: resolved?.id ?? null });
            onCellResolved?.(resolved);
          }}
          onEnter={() => qtyRef?.current?.focus()}
        />

        <Input
          ref={qtyRef}
          mono
          aria-label={t('stock.colQty')}
          inputMode="decimal"
          autoComplete="off"
          placeholder="0"
          value={line.qty}
          onChange={(event) => onChange({ qty: event.target.value })}
          onKeyDown={handleQtyKeyDown}
          error={error ?? undefined}
          suffix={unit === null ? undefined : unitText(unit)}
        />

        <span className="pt-2">
          <Badge tone={ready ? 'success' : 'neutral'}>
            {t(ready ? 'stock.lineReady' : 'stock.lineNotReady')}
          </Badge>
        </span>

        <button
          type="button"
          onClick={onRemove}
          disabled={!canRemove}
          aria-label={t('stock.removeLine', { index })}
          title={t('stock.removeLine', { index })}
          className={cn(
            'mt-1 flex size-8 items-center justify-center rounded-[var(--radius-sm)]',
            'text-text-faint transition-[background-color,color]',
            'duration-[var(--motion-fast)] ease-[var(--ease-standard)]',
            'hover:bg-danger-soft hover:text-danger',
            'disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent',
            'disabled:hover:text-text-faint',
          )}
        >
          <Trash2 size={16} aria-hidden />
        </button>
      </div>

      {/* Izoh butun kenglikni oladi: 13rem li katak ustuniga na manzil, na
          "Hozir … → kirimdan keyin …" sig'adi. Chap chekinish — birinchi
          ustun (2rem) va to'r oralig'i (0.75rem): izoh nomenklatura
          ustunidan boshlanadi. */}
      {/*
        AJRATILGAN O'RINLAR — javob ko'rinib turadi.
        Ilgari u katak kodi maydonining ochiladigan ro'yxatida edi va
        maydonga bosmagunicha ko'rinmasdi: javob borligini bilgan
        odamgina topardi.
      */}
      <ReservedCells
        warehouseId={warehouseId}
        nomenclatureId={nomenclature?.id ?? null}
        selectedCode={line.cellCode}
        onPick={(code) => {
          onChange({ cellCode: code });
          // Fokus miqdorga emas, KATAK maydoniga: kod tekshirilishi
          // kerak va odam natijani o'sha yerda ko'radi.
          cellRef?.current?.focus();
        }}
        className="pt-1.5 pl-[2.75rem]"
      />

      {noteParts.length === 0 && othersText === null && reservedWarning === null ? null : (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pt-1 pl-[2.75rem]">
          {noteParts.length === 0 ? null : (
            <p aria-live="polite" className="text-xs text-text-muted">
              {noteParts.join(' · ')}
            </p>
          )}
          {othersText === null ? null : (
            <p className="flex items-center gap-1.5 text-xs text-warn">
              <TriangleAlert size={13} aria-hidden />
              {othersText}
            </p>
          )}
          {reservedWarning === null ? null : (
            <p aria-live="polite" className="flex items-center gap-1.5 text-xs text-warn">
              <TriangleAlert size={13} aria-hidden />
              {reservedWarning}
            </p>
          )}
        </div>
      )}
    </li>
  );
};
